import { useQuery } from '@apollo/client'
import { Box, Text, Chip } from '@truepill/react-capsule'
import { LaunchDarkly } from '@truepill/tpos-types'
import { ReactComponent as IceIcon } from 'assets/icons/ice.svg'
import { ReactComponent as InventoryLocationIcon } from 'assets/icons/inventory-location.svg'
import { PV1Card } from 'components/Card'
import CriticalMedicalNoteWarning from 'components/CriticalMedicalNoteWarning'
import RxLastFill from 'components/RxLastFill'
import { usePrescribedData } from 'hooks/usePrescribedData'
import { useFlag } from 'providers/LaunchDarklyProvider'
import styled from 'styled-components'
import { primaryBackgroundColor, capsuleRed } from 'styles/styleVariables'
import type { Fill, NdcFullInfoPackage, Prescription, RXFillRequest } from 'types'
import { getNdcText, isRxFirstFill } from 'utils'
import { GET_NDC_PACKAGE } from './ReviewPrescription'

type PV1MedicationCardProps = {
  prescription: Prescription
  fill?: Fill
  rxFillRequest?: RXFillRequest
}

const PV1MedicationCard = ({ prescription, fill, rxFillRequest }: PV1MedicationCardProps): JSX.Element => {
  const { rxMedicationData } = usePrescribedData({ prescription })

  const isFirstFill = isRxFirstFill(prescription)
  const isColdChain = prescription.handlingTags.isColdChain
  const warehouseLocation = rxFillRequest?.fill?.dispensed?.ffsInventoryInfo?.warehouseLocation
  const tempDisplayPackSizeWithNdc = useFlag(LaunchDarkly.FeatureFlags.TEMP_DISPLAY_PACK_SIZE_WITH_NDC)
  const hyphenateNdc = useFlag(LaunchDarkly.FeatureFlags.TEMP_HYPHENATE_NDCS)

  const ndcs = [
    ...new Set([
      ...(fill?.dispensed?.ndc ? [fill.dispensed.ndc] : []),
      ...(rxMedicationData?.ndc ? [rxMedicationData.ndc] : []),
    ]),
  ]

  const { data } = useQuery(GET_NDC_PACKAGE, {
    variables: {
      ndcs,
    },
  })

  const ndcPackages = data?.getNdcPackage as NdcFullInfoPackage[]
  const prescribedNdcPackage = ndcPackages?.find(ndcPackage => ndcPackage?.ndc === rxMedicationData?.ndc)

  const ndcText = getNdcText(prescribedNdcPackage, tempDisplayPackSizeWithNdc, hyphenateNdc, rxMedicationData?.ndc)

  // mock dispensed data
  const dispensedPrescriptionData = {
    ...rxMedicationData,
  }

  return (
    <PV1Card
      data-testid="PV1MedicationCard"
      title={
        <Box css={{ display: 'flex', gap: '0.625rem' }}>
          MEDICATION
          <InventoryLocationChip warehouseLocation={warehouseLocation} />
          <ColdChainChip isColdChain={isColdChain} />
          <FirstFillChip isFirstFill={isFirstFill} />
        </Box>
      }
      rows={[
        {
          label: 'Medication:',
          prescribed: {
            value: (
              <DrugNameWithWarning>
                {rxMedicationData?.medication}
                <CriticalMedicalNoteWarning ndc={rxMedicationData?.ndc ?? ''} />
              </DrugNameWithWarning>
            ),
          },
          dispensed: { value: dispensedPrescriptionData.medication },
          testId: 'medication',
        },
        {
          label: 'NDC:',
          prescribed: { value: ndcText },
          dispensed: { value: dispensedPrescriptionData.ndc },
          testId: 'ndc',
        },
        {
          label: 'Quantity:',
          prescribed: { value: rxMedicationData?.quantity },
          dispensed: { value: dispensedPrescriptionData.quantity },
          testId: 'quantity',
        },
        {
          label: 'Requested qty:',
          prescribed: { value: rxFillRequest?.requestedMedication?.quantityRequested },
          fullWidth: true,
          testId: 'requested-quantity',
        },
        {
          label: 'Day Supply:',
          prescribed: { value: rxMedicationData?.ds },
          dispensed: { value: dispensedPrescriptionData.ds },
          testId: 'days-supply',
        },
        {
          label: 'Directions (SIG):',
          prescribed: { value: rxMedicationData?.sig },
          dispensed: { value: dispensedPrescriptionData.sig },
          testId: 'directions',
        },
        {
          label: 'Fills written:',
          prescribed: { value: rxMedicationData?.numberOfRefills },
          dispensed: { value: dispensedPrescriptionData.numberOfRefills },
          testId: 'refills',
        },
        {
          label: 'Qty remaining:',
          prescribed: { value: rxMedicationData?.quantityRemaining },
          dispensed: { value: dispensedPrescriptionData.quantityRemaining },
          testId: 'remaining',
        },
        {
          label: 'DAW:',
          prescribed: { value: rxMedicationData?.daw },
          fullWidth: true,
          testId: 'daw',
        },
        {
          label: 'Last filled:',
          prescribed: { value: <RxLastFill prescription={prescription} /> },
          fullWidth: true,
          testId: 'last-fill-date',
        },
        {
          label: 'Written date:',
          prescribed: { value: rxMedicationData?.writtenDate },
          dispensed: { value: dispensedPrescriptionData.writtenDate },
          isDate: true,
          testId: 'written-date',
        },
        {
          label: 'Expiration date:',
          prescribed: { value: rxMedicationData?.expirationDate },
          isDate: true,
          fullWidth: true,
          testId: 'expiration-date',
        },
        {
          label: 'Prescriber notes:',
          prescribed: { value: rxMedicationData?.prescriberNotes },
          fullWidth: true,
          testId: 'notes',
        },
        {
          label: 'Client notes:',
          prescribed: { value: rxMedicationData?.clientNotes },
          fullWidth: true,
          testId: 'notes',
        },
      ]}
    />
  )
}

const DrugNameWithWarning = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 0.25rem;
`

const InventoryLocationChip = ({ warehouseLocation }: { warehouseLocation?: string }): JSX.Element => {
  return (
    <Chip
      css={{
        backgroundColor: primaryBackgroundColor,
        color: 'var(--cap-colors-typography-dark)',
        '&>p': { display: 'flex', alignItems: 'center', gap: '0.250rem' },
      }}
    >
      <InventoryLocationIcon />
      <Text variant="chip">{warehouseLocation ?? 'NO LOCATION'}</Text>
    </Chip>
  )
}

const ColdChainChip = ({ isColdChain }: { isColdChain?: boolean }): JSX.Element => {
  if (!isColdChain) return <></>
  return (
    <Chip
      css={{
        backgroundColor: primaryBackgroundColor,
        '&>p': { paddingLeft: '0.125rem', paddingRight: '0.125rem' },
      }}
    >
      <IceIcon />
    </Chip>
  )
}

const FirstFillChip = ({ isFirstFill }: { isFirstFill?: boolean }): JSX.Element => {
  if (!isFirstFill) return <></>
  return (
    <Chip
      css={{
        backgroundColor: capsuleRed,
        '&>p': { display: 'flex' },
        color: 'var(--cap-colors-typography-white)',
      }}
    >
      <Text variant="chip">First Fill</Text>
    </Chip>
  )
}

export default PV1MedicationCard
